import { apiService } from '../api';
import { AxiosRequestConfig } from 'axios';
import type {
  TeamCreateRequest,
  CreateClanWithTeamRequest,
  CreateClanWithTeamResponse,
  InviteLinkResponse,
} from '@/types/clan';
import { ApiResponse } from '@/types/apiResponse';
import { Team, GetTeamByIdOptions } from '@/types/team';
import type { SocialLinkInput } from '@/types/socialLinks';
import type {
  TeamProfileAchievementsApiResponse,
  TeamProfileApiResponse,
  TeamProfileHistoryApiResponse,
} from '@/types/teamProfile';
import type {
  ApiSuccessResponse,
  SponsorPayload,
  SponsorResponse,
} from '@/types/tournament/api';

export type { GetTeamByIdOptions };

export interface GenerateTeamInviteLinkRequest {
  contextId: string;
  role: string;
  type: 'team';
}

export interface TeamCreateResponse {
  logoPresignedUrl?: string;
}

export interface TeamCreateAPIResponse {
  query: string;
  result: TeamCreateResponse;
  error: string | null;
  totalResults: number | null;
  pageNo: number | null;
  message: string;
}

export interface TeamUpdateImageRequest {
  logo: {
    name: string;
    size: number;
    type: string;
    url: string;
  };
  logoUpload: boolean;
}

export interface TeamUpdateMembersPayload {
  members: {
    players: string[];
    substitutes?: string[];
    captain: string;
    [key: string]: unknown;
  };
}

export interface AddToTeamRequest {
  name?: string;
  email?: string;
  mobileNo?: {
    code: string;
    number: string;
    name?: string | null;
    abbreviation?: string | null;
  };
  inGameId?: string;
  inGameName?: string;
  role: string;
}

/** PUT /team/:id/member — registration payload nested under `fields` for API validation. */
export interface UpdateMemberDetailsRequest {
  role: string;
  fields: Omit<AddToTeamRequest, 'role'>;
}

export interface LeaveTeamRequest {
  teamId: string;
  userId: string;
  newCaptainId?: string;
}

class TeamService {
  private static instance: TeamService;

  private constructor() {}

  public static getInstance(): TeamService {
    if (!TeamService.instance) {
      TeamService.instance = new TeamService();
    }
    return TeamService.instance;
  }

  /**
   * When `gameId` is omitted, returns all teams you belong to in the clan (all games).
   */
  async getMyTeams(
    clanId: string,
    gameId?: string,
  ): Promise<ApiResponse<Team[]>> {
    const params = new URLSearchParams({ clanId });
    if (gameId !== undefined && gameId !== '') {
      params.set('gameId', gameId);
    }
    return apiService.get<ApiResponse<Team[]>>(
      `/private/api/v1/team/getMyTeams?${params.toString()}`,
    );
  }

  async getTeamsByClanId(clanId: string): Promise<ApiResponse<Team[]>> {
    return apiService.get<ApiResponse<Team[]>>(
      `/private/api/v2/team/getTeams?clanId=${encodeURIComponent(clanId)}`,
    );
  }

  /**
   * `inviteId` lets an invitee load the team before joining it.
   * Pass `includeSponsors: true` only on surfaces that render sponsors (e.g. TeamDetail).
   */
  async getTeamById(
    teamId: string,
    options?: GetTeamByIdOptions,
  ): Promise<ApiResponse<Team>> {
    const params = new URLSearchParams({ _id: teamId });
    if (options?.includeSponsors) {
      params.set('includeSponsors', 'true');
    }
    if (options?.inviteId) {
      params.set('inviteId', options.inviteId);
    }
    return apiService.get<ApiResponse<Team>>(
      `/private/api/v2/team/getTeams?${params.toString()}`,
    );
  }

  async setTeamSocialLinks(
    teamId: string,
    socialLinks: SocialLinkInput[],
  ): Promise<ApiResponse<Team>> {
    return apiService.put<
      ApiResponse<Team>,
      { socialLinks: SocialLinkInput[] }
    >(`/private/api/v1/team/${encodeURIComponent(teamId)}/social-links`, {
      socialLinks,
    });
  }

  async createTeamSponsor(
    teamId: string,
    sponsor: Omit<SponsorPayload, 'scope' | 'tournamentId'>,
  ): Promise<ApiResponse<SponsorResponse>> {
    return apiService.requestWithErrorHandling<ApiResponse<SponsorResponse>>({
      method: 'POST',
      url: `/private/api/v1/team/${encodeURIComponent(teamId)}/sponsors`,
      data: sponsor,
    });
  }

  async updateTeamSponsor(
    teamId: string,
    sponsorId: string,
    sponsor: Omit<SponsorPayload, 'scope' | 'tournamentId'>,
  ): Promise<ApiResponse<SponsorResponse>> {
    return apiService.requestWithErrorHandling<ApiResponse<SponsorResponse>>({
      method: 'PUT',
      url: `/private/api/v1/team/${encodeURIComponent(teamId)}/sponsors/${encodeURIComponent(sponsorId)}`,
      data: sponsor,
    });
  }

  async deleteTeamSponsor(
    teamId: string,
    sponsorId: string,
  ): Promise<ApiSuccessResponse> {
    return apiService.delete(
      `/private/api/v1/team/${encodeURIComponent(teamId)}/sponsors/${encodeURIComponent(sponsorId)}`,
    );
  }

  async checkTeamSlugAvailability(
    slug: string,
    excludeTeamId?: string | null,
  ): Promise<ApiResponse<{ available: boolean; normalized: string }>> {
    const encoded = encodeURIComponent(slug);
    const query =
      excludeTeamId && excludeTeamId.length > 0
        ? `?teamId=${encodeURIComponent(excludeTeamId)}`
        : '';
    return apiService.get<
      ApiResponse<{ available: boolean; normalized: string }>
    >(`/private/api/v2/team/slug-available/${encoded}${query}`);
  }

  async updateTeamProfile(
    teamId: string,
    data: { name?: string; teamSlug?: string },
  ): Promise<ApiResponse<Team>> {
    return apiService.put<
      ApiResponse<Team>,
      { name?: string; teamSlug?: string }
    >(`/private/api/v2/team/${encodeURIComponent(teamId)}`, data);
  }

  async generateInviteLink(
    teamId: string,
    data: GenerateTeamInviteLinkRequest,
    config?: AxiosRequestConfig,
  ): Promise<InviteLinkResponse> {
    const url = `/private/api/v1/team/${teamId}/generateInviteLink`;
    if (config !== undefined) {
      return apiService.post<InviteLinkResponse, GenerateTeamInviteLinkRequest>(
        url,
        data,
        config,
      );
    }
    return apiService.post<InviteLinkResponse, GenerateTeamInviteLinkRequest>(
      url,
      data,
    );
  }

  async createTeam(
    teamData: TeamCreateRequest,
  ): Promise<TeamCreateAPIResponse> {
    return apiService.post<TeamCreateAPIResponse, TeamCreateRequest>(
      '/private/api/v2/team/create',
      teamData,
    );
  }

  async updateImage(
    teamId: string,
    data: TeamUpdateImageRequest,
  ): Promise<ApiResponse<{ logoPresignedUrl?: string }>> {
    return apiService.put<
      ApiResponse<{ logoPresignedUrl?: string }>,
      TeamUpdateImageRequest
    >(`/private/api/v1/team/updateTeam/${teamId}`, data);
  }

  async updateTeam(
    teamId: string,
    data: TeamUpdateMembersPayload,
  ): Promise<ApiResponse<Team>> {
    return apiService.put<ApiResponse<Team>, TeamUpdateMembersPayload>(
      `/private/api/v1/team/updateTeam/${teamId}`,
      data,
    );
  }

  async deleteTeam(teamId: string): Promise<unknown> {
    return apiService.delete<unknown>(
      `/private/api/v1/team/deleteTeam/${teamId}`,
    );
  }

  async removeFromTeam(
    teamId: string,
    userId: string,
    role: string,
    newCaptainId?: string,
  ): Promise<ApiResponse<Team>> {
    return apiService.post<
      ApiResponse<Team>,
      { teamId: string; userId: string; role: string; newCaptainId?: string }
    >('/private/api/v1/team/removeFromTeam', {
      teamId,
      userId,
      role,
      newCaptainId,
    });
  }

  async addToTeam(teamId: string, data: AddToTeamRequest): Promise<unknown> {
    return apiService.post<unknown, AddToTeamRequest>(
      `/private/api/v1/team/${teamId}/addToTeam`,
      data,
    );
  }

  async updateMemberDetails(
    teamId: string,
    data: UpdateMemberDetailsRequest,
  ): Promise<ApiResponse<Team>> {
    return apiService.put<ApiResponse<Team>, UpdateMemberDetailsRequest>(
      `/private/api/v1/team/${teamId}/member`,
      data,
    );
  }

  async leaveTeam(data: LeaveTeamRequest): Promise<ApiResponse<Team>> {
    return apiService.post<ApiResponse<Team>, LeaveTeamRequest>(
      '/private/api/v1/team/leaveTeam',
      data,
    );
  }

  async createClanWithTeam(
    data: CreateClanWithTeamRequest,
  ): Promise<CreateClanWithTeamResponse> {
    return apiService.post<
      CreateClanWithTeamResponse,
      CreateClanWithTeamRequest
    >('/private/api/v1/team/createClanWithTeam', data);
  }

  /**
   * Public team profile (hero + performance stats).
   * `teamIdentifier` may be a Mongo ObjectId or `teamSlug`.
   */
  async getTeamProfile(
    teamIdentifier: string,
  ): Promise<TeamProfileApiResponse> {
    const encodedIdentifier = encodeURIComponent(teamIdentifier);
    return apiService.get<TeamProfileApiResponse>(
      `/private/api/v2/team/${encodedIdentifier}/team-profile`,
    );
  }

  /**
   * Paginated achievements. `pageNo` is 0-based (API contract); UI pages are 1-based.
   */
  async getTeamAchievements(
    teamIdentifier: string,
    pageNo: number,
    limit: number,
  ): Promise<TeamProfileAchievementsApiResponse> {
    const encodedIdentifier = encodeURIComponent(teamIdentifier);
    const params = new URLSearchParams({
      pageNo: String(pageNo),
      limit: String(limit),
    });
    return apiService.get<TeamProfileAchievementsApiResponse>(
      `/private/api/v2/team/${encodedIdentifier}/achievements?${params.toString()}`,
    );
  }

  /**
   * Paginated tournament history. `pageNo` is 0-based.
   */
  async getTeamTournamentHistory(
    teamIdentifier: string,
    pageNo: number,
    limit: number,
  ): Promise<TeamProfileHistoryApiResponse> {
    const encodedIdentifier = encodeURIComponent(teamIdentifier);
    const params = new URLSearchParams({
      pageNo: String(pageNo),
      limit: String(limit),
    });
    return apiService.get<TeamProfileHistoryApiResponse>(
      `/private/api/v2/team/${encodedIdentifier}/tournament-history?${params.toString()}`,
    );
  }
}

export const teamService = TeamService.getInstance();
